import { pipe, Option, Array as A } from 'effect';
import { AbilityBuilder, createMongoAbility } from '@casl/ability';
import type { RawRuleOf } from '@casl/ability';
import type { CompanySettingsAccessContext } from '@/abilities/companySettings/context';
import { COMPANY_SETTINGS_DESTINATION, type CompanySettingsAbility } from '@/abilities/companySettings/vocabulary';
import { ERP_NAMES } from '@/domain/companies/helpers';

const POLICY_SETTINGS_UNSUPPORTED_ERPS = [
  ERP_NAMES.qbo,
  ERP_NAMES.qbd,
  ERP_NAMES.xero,
  ERP_NAMES.sage,
  ERP_NAMES.sageSA,
];

// The sections are shown on the role and open with the features, so the tab stays shown, with its stub, while a
// feature is off.
export const companySettingsVendorPreferencesRules = ({
  isAdmin,
  isAccountant,
  hasVendorPreferences,
  hasFinancialDocuments,
  hasVendorBasedPolicyManagement,
  connectedErp,
}: Pick<
  CompanySettingsAccessContext,
  | 'isAdmin'
  | 'isAccountant'
  | 'hasVendorPreferences'
  | 'hasFinancialDocuments'
  | 'hasVendorBasedPolicyManagement'
  | 'connectedErp'
>): RawRuleOf<CompanySettingsAbility>[] => {
  const { can, rules } = new AbilityBuilder<CompanySettingsAbility>(createMongoAbility);
  if (isAdmin || isAccountant) {
    const isPolicySettingsErp = !pipe(
      Option.fromNullable(connectedErp),
      Option.exists((erp) => A.contains(POLICY_SETTINGS_UNSUPPORTED_ERPS, erp)),
    );
    can('see', COMPANY_SETTINGS_DESTINATION, { destination: 'vendorPreferences.general' });
    if (isPolicySettingsErp) can('see', COMPANY_SETTINGS_DESTINATION, { destination: 'vendorPreferences.policies' });
    if (hasVendorPreferences && hasFinancialDocuments) {
      can('open', COMPANY_SETTINGS_DESTINATION, { destination: 'vendorPreferences.general' });
      if (isPolicySettingsErp && hasVendorBasedPolicyManagement) {
        can('open', COMPANY_SETTINGS_DESTINATION, { destination: 'vendorPreferences.policies' });
      }
    }
  }
  return rules;
};
